import { useTheme } from "lib/theme";
import React, { forwardRef, useMemo, useState } from "react";
import ReactDatePicker from "react-datepicker";
import { MdOutlineDateRange } from "react-icons/md";
import formatDate from "date-fns/format";

const DatePickerCustomInput = React.forwardRef<
  HTMLInputElement,
  React.HTMLProps<HTMLInputElement> & {
    isDisabled?: boolean;
    isErrored?: boolean;
    placeholder?: string;
    active?: boolean;
    date?: Date;
    includeTime: boolean;
  }
>(
  (
    { date, includeTime, onClick, isDisabled, isErrored, placeholder, active },
    ref
  ) => {
    const theme = useTheme();
    return (
      <div
        data-disabled={isDisabled}
        data-errored={isErrored}
        data-active={active}
        className="flex h-12 w-full cursor-pointer items-center gap-2.5 rounded-lg border-2 border-zi-lightGray bg-zi-superLightGray pl-2.5 text-base text-zi-text data-[disabled=true]:cursor-default data-[active=true]:border-zi-primary data-[errored=true]:border-zi-formError"
        onClick={onClick}
      >
        <MdOutlineDateRange size={20} color={theme.app.colors.mediumGray} />
        <input
          style={{ all: "unset", flexGrow: 1 }}
          ref={ref}
          disabled={true}
          value={
            date
              ? `${formatDate(date, "MMM d, yyyy")} ${
                  includeTime ? formatDate(date, "hh:mm a") : ""
                }`
              : ""
          }
          placeholder={placeholder}
        />
      </div>
    );
  }
);
DatePickerCustomInput.displayName = "DatePickerCustomInput";

type Props = {
  dateFormat: string;
  includeTime: boolean;
  defaultToNow: boolean;
  value?: Date;
  onChange?: (value: Date | null) => void;
  placeholder?: string;
  isDisabled?: boolean;
  isErrored?: boolean;
};

export const DatePicker = forwardRef<HTMLInputElement, Props>(
  (
    {
      value,
      onChange,
      dateFormat,
      includeTime,
      placeholder,
      isDisabled,
      isErrored,
    },
    ref
  ) => {
    const [active, setActive] = useState(false);

    const handleChange = useMemo(() => {
      return (value: Date | null) => {
        if (onChange) {
          onChange(value);
        }
      };
    }, [onChange]);

    return (
      <div className="custom-datepicker relative">
        <ReactDatePicker
          wrapperClassName="DatePicker"
          popperClassName="DatePicker-Popper"
          dateFormat={includeTime ? dateFormat + " HH:mm" : dateFormat}
          placeholderText={placeholder}
          selected={value}
          onChange={handleChange}
          isClearable
          customInput={
            <DatePickerCustomInput
              ref={ref}
              isDisabled={isDisabled}
              isErrored={isErrored}
              placeholder={placeholder}
              active={active}
              date={value}
              includeTime={includeTime}
            />
          }
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          showTimeInput={includeTime}
          shouldCloseOnSelect={false}
          onCalendarOpen={() => setActive(true)}
          onCalendarClose={() => setActive(false)}
          disabled={isDisabled}
        />
      </div>
    );
  }
);

DatePicker.displayName = "DatePicker";
